import { useState, type FormEvent } from 'react';
import getSupabase from '@/lib/supabaseClient';

type Status = 'idle' | 'submitting' | 'success' | 'error';

const inputClass =
  'w-full px-4 py-3 rounded-lg border border-gray-300 bg-white text-sm text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500';

export default function ContactForm() {
  const [status, setStatus] = useState<Status>('idle');
  const [errorMessage, setErrorMessage] = useState('');

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const form = event.currentTarget;
    const formData = new FormData(form);

    const honeypot = String(formData.get('company_alt') ?? '').trim();
    if (honeypot) {
      setStatus('success');
      form.reset();
      return;
    }

    const firstName = String(formData.get('first_name') ?? '').trim();
    const lastName = String(formData.get('last_name') ?? '').trim();
    const email = String(formData.get('email') ?? '').trim();
    const comments = String(formData.get('comments') ?? '').trim();

    if (!firstName || !lastName || !email || !comments) {
      setErrorMessage('Please fill in every field before sending.');
      setStatus('error');
      return;
    }

    const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailPattern.test(email)) {
      setErrorMessage('Please enter a valid email address.');
      setStatus('error');
      return;
    }

    if (comments.length > 500) {
      setErrorMessage('Please keep your comments to 500 characters or fewer.');
      setStatus('error');
      return;
    }

    const supabase = getSupabase();
    if (!supabase) {
      setErrorMessage("Our inquiry system isn't connected yet. Please try again in a moment.");
      setStatus('error');
      return;
    }

    setStatus('submitting');
    setErrorMessage('');

    try {
      const { error } = await supabase.functions.invoke('contact-inquiry', {
        body: { first_name: firstName, last_name: lastName, email, comments },
      });

      if (error) {
        throw new Error(error.message || 'Something went wrong. Please try again.');
      }

      setStatus('success');
      form.reset();
    } catch (err) {
      setErrorMessage(
        err instanceof Error ? err.message : 'Something went wrong. Please try again.'
      );
      setStatus('error');
    }
  };

  if (status === 'success') {
    return (
      <div className="text-center py-16 md:py-24">
        <div className="w-16 h-16 md:w-20 md:h-20 mx-auto mb-6 flex items-center justify-center rounded-full bg-emerald-100">
          <i className="ri-check-line text-3xl md:text-4xl text-emerald-600"></i>
        </div>
        <h2 className="text-3xl md:text-5xl font-serif font-bold text-gray-900 leading-tight max-w-3xl mx-auto">
          Thank you, your inquiry has been received.
        </h2>
        <p className="text-xl md:text-2xl text-gray-600 mt-5">
          We will get back to you shortly.
        </p>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} className="w-full" noValidate>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
        <div>
          <label htmlFor="first_name" className="block text-sm font-medium text-gray-700 mb-2">
            First Name
          </label>
          <input
            id="first_name"
            name="first_name"
            type="text"
            autoComplete="given-name"
            className={inputClass}
            placeholder="Jane"
          />
        </div>
        <div>
          <label htmlFor="last_name" className="block text-sm font-medium text-gray-700 mb-2">
            Last Name
          </label>
          <input
            id="last_name"
            name="last_name"
            type="text"
            autoComplete="family-name"
            className={inputClass}
            placeholder="Doe"
          />
        </div>
      </div>

      <div className="mt-5">
        <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">
          Email Address
        </label>
        <input
          id="email"
          name="email"
          type="email"
          autoComplete="email"
          className={inputClass}
          placeholder="jane@example.com"
        />
      </div>

      <div className="mt-5">
        <label htmlFor="comments" className="block text-sm font-medium text-gray-700 mb-2">
          Comments
        </label>
        <textarea
          id="comments"
          name="comments"
          rows={5}
          maxLength={500}
          className={`${inputClass} resize-none`}
          placeholder="Tell us how we can help..."
        />
        <p className="text-xs text-gray-400 mt-1 text-right">500 character limit</p>
      </div>

      <div className="contact-form-check" aria-hidden="true">
        <input
          type="text"
          name="company_alt"
          tabIndex={-1}
          autoComplete="off"
          aria-hidden="true"
          readOnly
        />
      </div>

      {status === 'error' && errorMessage && (
        <p className="mt-5 text-sm text-red-600 bg-red-50 border border-red-200 rounded-lg px-4 py-3">
          {errorMessage}
        </p>
      )}

      <button
        type="submit"
        disabled={status === 'submitting'}
        className="mt-7 w-full sm:w-auto px-10 py-3.5 bg-emerald-600 text-white text-sm font-medium rounded-lg hover:bg-emerald-700 transition-colors disabled:opacity-60 disabled:cursor-not-allowed whitespace-nowrap cursor-pointer flex items-center justify-center gap-2"
      >
        {status === 'submitting' ? (
          <>
            <i className="ri-loader-4-line animate-spin text-base"></i>
            Sending...
          </>
        ) : (
          <>
            <i className="ri-send-plane-line text-base"></i>
            Send
          </>
        )}
      </button>
    </form>
  );
}